import React, { useState } from 'react';
import { Alert, Button, Input, Select } from 'antd';
import { CloseOutlined, SearchOutlined, TeamOutlined } from '@ant-design/icons';
import PageScaffold from '../../components/PageScaffold';
import useContactsPageController from './controllers/useContactsPageController';
import ContactsList from './components/ContactsList';
import ContactDrawer from './components/ContactDrawer';
import ContactsFilterDropdown from './components/ContactsFilterDropdown';
import AttachOrphansModal from './components/AttachOrphansModal';
import { CONTACTS_LAST_INTERACTION_OPTIONS, CONTACTS_SORT_OPTIONS } from './types/ContactDisplay';
import './ContactsPage.css';

export const CONTACTS_PAGE_PATH = '/contacts';
export const CONTACTS_PAGE_TITLE = 'Contacts';

const ContactsPage: React.FC = () => {
  const { state, actions } = useContactsPageController();
  const { filters } = state;
  const [attachingOwnerUid, setAttachingOwnerUid] = useState<string | null>(null);

  const sortLabel = CONTACTS_SORT_OPTIONS.find((option) => option.value === filters.sortBy)?.label;
  const lastInteractionLabel = CONTACTS_LAST_INTERACTION_OPTIONS.find((option) => option.value === filters.lastInteraction)?.label ?? '';

  return (
    <PageScaffold
      title={CONTACTS_PAGE_TITLE}
      subtitle="Retrouvez vos contacts et le fil de vos échanges."
      breadcrumbItems={[{ title: ' ' }]}
    >
      <div className="contacts">
        <div className="contacts__filters">
          <Input
            className="contacts__search"
            prefix={<SearchOutlined />}
            placeholder="Nom, numéro, entreprise, tag…"
            allowClear
            value={filters.search}
            onChange={(e) => actions.setSearch(e.target.value)}
          />
          <ContactsFilterDropdown
            mode="starred"
            label={`Tri : ${sortLabel}`}
            title={`Tri : ${sortLabel}`}
            value={filters.sortBy}
            options={CONTACTS_SORT_OPTIONS}
            onChange={actions.setSortBy}
            starredValue={state.defaultSortBy}
            onStar={actions.setDefaultSortBy}
            footer="L'étoile mémorise votre tri par défaut sur cet appareil."
          />
          <ContactsFilterDropdown
            mode="multiple"
            label="Chaleur"
            title="Chaleur"
            value={filters.heat}
            options={state.heatOptions}
            onChange={actions.setHeat}
          />
          <ContactsFilterDropdown
            mode="multiple"
            label="Type"
            title="Type"
            value={filters.types}
            options={state.typeOptions}
            onChange={actions.setTypes}
          />
          <ContactsFilterDropdown
            mode="multiple"
            label="Tags"
            title="Tags"
            value={filters.tags}
            options={state.tagOptions}
            onChange={actions.setTags}
          />
          <ContactsFilterDropdown
            mode="single"
            label={filters.lastInteraction === 'any' ? 'Dernière interaction' : lastInteractionLabel}
            title="Dernière interaction"
            value={filters.lastInteraction}
            options={CONTACTS_LAST_INTERACTION_OPTIONS}
            onChange={actions.setLastInteraction}
          />
          {state.canSelectSubscription && (
            <Select
              className="contacts__filter contacts__filter--scope"
              prefix={<TeamOutlined />}
              showSearch
              optionFilterProp="label"
              value={filters.subscriptionId ?? ''}
              options={state.subscriptionOptions}
              onChange={actions.setSubscriptionId}
            />
          )}
        </div>

        {state.filterChips.length > 0 && (
          <div className="contacts__active-filters">
            {state.filterChips.map((chip) => (
              <span key={`${chip.key}-${chip.value}`} className="contacts__active-filter">
                {chip.label}
                <button
                  type="button"
                  className="contacts__active-filter-remove"
                  aria-label={`Retirer ${chip.label}`}
                  onClick={() => actions.removeFilterChip(chip)}
                >
                  <CloseOutlined />
                </button>
              </span>
            ))}
            <Button type="text" className="contacts__active-filters-clear" onClick={actions.clearFilterChips}>
              Tout effacer
            </Button>
          </div>
        )}

        {state.isOrphansScope && (
          <div className="contacts__orphans">
            {state.orphanOwners.map((owner) => (
              <Alert
                key={owner.uid}
                type="warning"
                showIcon
                message={`${owner.count} contact${owner.count > 1 ? 's' : ''} ${owner.ofName}${owner.hasLeft ? ", qui ne fait plus partie de l'entreprise," : ''} n'${owner.count > 1 ? 'ont' : 'a'} plus de numéro.`}
                action={(
                  <Button size="small" onClick={() => setAttachingOwnerUid(owner.uid)}>
                    Rattacher à un numéro…
                  </Button>
                )}
              />
            ))}
          </div>
        )}

        <div className="contacts__count">
          {state.isLoading ? ' ' : `${state.contactsCount} contact${state.contactsCount > 1 ? 's' : ''}`}
        </div>

        <div className="contacts__content">
          {/* Remounted on each filter change: back to the top with the first rows only */}
          <ContactsList
            key={JSON.stringify(filters)}
            groups={state.groups}
            selectedContactId={state.selectedContact?.id ?? null}
            isLoading={state.isLoading}
            isLoadingMore={state.isLoadingMore}
            showAlphabetIndex={filters.sortBy === 'alphabetical'}
            onSelect={actions.selectContact}
          />
        </div>
      </div>

      <ContactDrawer contact={state.selectedContact} onClose={() => actions.selectContact(null)} />

      <AttachOrphansModal
        owner={state.orphanOwners.find((owner) => owner.uid === attachingOwnerUid) ?? null}
        targets={state.attachTargets}
        isLoading={state.isAttaching}
        onCancel={() => setAttachingOwnerUid(null)}
        onConfirm={async (ownerUid, subscriptionId) => {
          await actions.attachOrphans(ownerUid, subscriptionId);
          setAttachingOwnerUid(null);
        }}
      />
    </PageScaffold>
  );
};

export default ContactsPage;
